"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Phone, Mail, MessageSquare, Loader, ArrowRight, Sparkles } from "lucide-react";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in duration-500">
        <div className="size-16 rounded-lg bg-primary/20 flex items-center justify-center mb-4">
          <Sparkles className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-bold text-foreground">Application Received!</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-8 border-primary/50 hover:bg-primary/10 rounded-lg">
          Submit another inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-default font-semibold uppercase tracking-wider text-muted-foreground">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" className="bg-background border-border focus-visible:border-primary focus-visible:ring-primary/30 transition-all duration-300 rounded-md" {...field} />
              </FormControl>
              <FormDescription className="text-[10px]">Enter your legal business name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-default font-semibold uppercase tracking-wider text-muted-foreground">Business Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@company.com" className="bg-background border-border focus-visible:border-primary focus-visible:ring-primary/30 transition-all duration-300 rounded-md" {...field} />
              </FormControl>
              <FormDescription className="text-[10px]">We will contact you at this address.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-default font-semibold uppercase tracking-wider text-muted-foreground">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 000-0000" className="bg-background border-border focus-visible:border-primary focus-visible:ring-primary/30 transition-all duration-300 rounded-md" {...field} />
              </FormControl>
              <FormDescription className="text-[10px]">Best number for a quick call.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="inquiry_type" rules={{
        required: "Please select your business type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-default font-semibold uppercase tracking-wider text-muted-foreground">Business Type</FormLabel>
              <FormControl>
                <select className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-base shadow-sm transition-all duration-300 outline-none focus-visible:border-primary focus-visible:ring-[3px] focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm appearance-none" {...field}>
                  <option value="" disabled className="bg-background text-muted-foreground">Select your industry</option>
                  <option value="Landscaper" className="bg-background text-foreground">Landscaper / Hardscaper</option>
                  <option value="Pool Builder" className="bg-background text-foreground">Pool Builder</option>
                  <option value="General Contractor" className="bg-background text-foreground">General Contractor</option>
                  <option value="Other" className="bg-background text-foreground">Other Professional</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Please tell us about your business"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs font-default font-semibold uppercase tracking-wider text-muted-foreground">Project Goals</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your current volume and why you're interested in ClifRock..." className="min-h-[120px] bg-background border-border focus-visible:border-primary focus-visible:ring-primary/30 transition-all duration-300 resize-none rounded-md" {...field} />
            </FormControl>
            <FormDescription className="text-[10px]">Briefly describe your typical outdoor living projects.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto group bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 rounded-lg font-default font-bold" size="lg">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Processing...
          </> : <>
            Become a ClifRock Installer
            <ArrowRight className="ml-2 size-4 group-hover:translate-x-1 transition-transform" />
          </>}
      </Button>
    </Form>;
}
export default function StartYourPartnership() {
  return <section data-section-id="250020" id="start-your-partnership" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/contractor_handshake_bg.png')] bg-cover bg-center opacity-20 blur-sm" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent" />
        <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/industrial_noise.png')] opacity-[0.05] mix-blend-overlay" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-default font-bold mb-4 tracking-tight">
            Start Your <span className="text-primary">Partnership</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg font-default">
            Join the elite network of ClifRock installers and transform your business with faster builds and higher margins.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          
          {}
          <div className="lg:col-span-4 flex flex-col">
            <div className="bg-card/60 backdrop-blur-md border border-border rounded-lg p-8 h-full flex flex-col justify-between shadow-xl relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-100 transition-opacity duration-500" />
              
              <div className="relative z-10">
                <h3 className="text-2xl font-default font-bold mb-8 flex items-center gap-3">
                  <MessageSquare className="text-primary size-6" />
                  Industry Certified
                </h3>
                
                <div className="space-y-8">
                  <div className="flex items-start gap-4">
                    <div className="size-10 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                      <Phone className="size-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs font-default font-semibold uppercase tracking-wider text-muted-foreground mb-1">Direct Support</p>
                      <p className="text-lg font-default font-bold">(800) 555-ROCK</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="size-10 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                      <Mail className="size-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs font-default font-semibold uppercase tracking-wider text-muted-foreground mb-1">Partnership Inquiry</p>
                      <p className="text-lg font-default font-bold">partners@clifrock.com</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-12 pt-8 border-t border-border relative z-10">
                <p className="text-xs font-default font-semibold uppercase tracking-wider text-muted-foreground mb-6 text-center">Trusted by Professionals</p>
                <div className="grid grid-cols-2 gap-4 items-center justify-center opacity-80 grayscale hover:grayscale-0 transition-all duration-500">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/hgtv_logo_white.png" alt="HGTV Featured" className="h-8 object-contain mx-auto" loading="lazy" />
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/diy_network_logo.png" alt="DIY Network" className="h-8 object-contain mx-auto" loading="lazy" />
                </div>
                <div className="mt-6 flex justify-center">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/five_star_rating.png" alt="5 Star Rated" className="h-6 object-contain" loading="lazy" />
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-8">
            <div className="bg-card backdrop-blur-sm border border-border rounded-lg p-8 md:p-12 shadow-2xl h-full">
              <div className="mb-8">
                <h3 className="text-2xl font-default font-bold mb-2">Request Contractor Information</h3>
                <p className="text-muted-foreground font-default">Complete the form below to receive our installer opportunity guide and training schedule.</p>
              </div>
              
              <FormProvider formKey="start_partnership_form_home" formId="start_partnership_form_home" sectionName="StartYourPartnership" defaultValues={{
              name: "",
              email: "",
              phone: "",
              inquiry_type: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              inquiry_type: {
                required: true
              },
              message: {
                required: true
              }
            }} submitText="Become a ClifRock Installer" successMessage="Thank you for your interest! A ClifRock partnership specialist will contact you within 24 business hours.">
                <ContactFormInner />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}